import React from "react";
import type {
  EquipmentAttributeDefinition,
  EquipmentModel,
} from "../../../api/equipment/Equipment.typed";

type SortOrder = "ASC" | "DESC";

interface UseEquipmentSortArgs {
  selectedModel: EquipmentModel | undefined;
  onSortChange: (sortBy: string | undefined, sortOrder: SortOrder | undefined) => void;
}

export function useEquipmentSort({ selectedModel, onSortChange }: UseEquipmentSortArgs) {
  const [sortBy, setSortBy] = React.useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = React.useState<SortOrder | undefined>(undefined);

  const effectiveSortOrder: SortOrder = React.useMemo(() => {
    if (sortOrder) return sortOrder;

    return sortBy ? "ASC" : "DESC";
  }, [sortBy, sortOrder]);

  const sortableAttributes = React.useMemo(() => {
    if (!selectedModel) return [];

    return (selectedModel.Attributes || []).filter(
      (a: EquipmentAttributeDefinition) =>
        a.Type === "string" || a.Type === "number" || a.Type === "date",
    );
  }, [selectedModel]);

  const currentSortAttr = React.useMemo(() => {
    if (!sortBy || !selectedModel) return undefined;

    return (selectedModel.Attributes || []).find(
      (a: EquipmentAttributeDefinition) => a.ID === sortBy,
    );
  }, [sortBy, selectedModel]);

  const pickSort = React.useCallback(
    (attrId?: string, order?: SortOrder) => {
      if (!selectedModel) return;

      if (!attrId) {
        const nextOrder: SortOrder = order ?? "DESC";
        setSortBy(undefined);
        setSortOrder(nextOrder);
        onSortChange(undefined, nextOrder);

        return;
      }

      const nextOrder: SortOrder = order ?? "ASC";
      setSortBy(attrId);
      setSortOrder(nextOrder);
      onSortChange(attrId, nextOrder);
    },
    [selectedModel, onSortChange],
  );

  const toggleSortOrder = React.useCallback(() => {
    if (!selectedModel) return;
    const nextOrder: SortOrder = effectiveSortOrder === "ASC" ? "DESC" : "ASC";
    setSortOrder(nextOrder);
    onSortChange(sortBy, nextOrder);
  }, [selectedModel, effectiveSortOrder, sortBy, onSortChange]);

  const resetSort = React.useCallback(() => {
    setSortBy(undefined);
    setSortOrder(undefined);
  }, []);

  return {
    sortBy,
    sortOrder,
    effectiveSortOrder,
    sortableAttributes,
    currentSortAttr,
    pickSort,
    toggleSortOrder,
    resetSort,
  };
}
